import { supabase } from '../lib/supabase'
import {
  InformativoItem,
  CreateInformativoPayload,
  InformativoVersion,
  NewsLikeSummary,
  NewsLiker,
} from '../types/informativo'

/**
 * Lista todos os itens de notícias ordenados por data (mais recente primeiro)
 * @param type Tipo de notícia ('circulando', 'imprensa', 'interno' ou 'boletim'). Se não fornecido, retorna todos
 * @returns Promise<InformativoItem[]> Array de itens
 */
export async function listItems(type?: 'circulando' | 'imprensa' | 'interno' | 'boletim'): Promise<InformativoItem[]> {
  let query = supabase
    .from('news')
    .select('*')
    .is('deleted_at', null) // Apenas registros não deletados
    .order('date', { ascending: false })
    .order('created_at', { ascending: false })

  if (type) {
    query = query.eq('type', type)
  }

  const { data, error } = await query

  if (error) {
    throw new Error(`Erro ao carregar itens: ${error.message}`)
  }

  return data || []
}

/**
 * Cria um novo item
 * @param payload Dados do item a ser criado
 * @returns Promise<string> ID do item criado
 */
export async function createItem(payload: CreateInformativoPayload): Promise<string> {
  // Obter usuário atual
  const { data: { user } } = await supabase.auth.getUser()
  
  // Preparar dados base para inserção
  const baseData: any = {
    date: payload.date,
    title: payload.title,
    description: payload.description,
    file: payload.file,
    link: payload.link,
    type: payload.type
  }
  
  // Tentar inserir com created_by primeiro (se usuário estiver autenticado)
  let insertData = { ...baseData }
  if (user?.id) {
    insertData.created_by = user.id
  }
  
  let { data, error } = await supabase
    .from('news')
    .insert(insertData)
    .select('id')
    .single()

  // Se der erro relacionado à coluna created_by não existir, tentar sem ela
  if (error && (error.message.includes("created_by") || error.message.includes("schema cache"))) {
    // Tentar novamente sem created_by
    const { data: retryData, error: retryError } = await supabase
      .from('news')
      .insert(baseData)
      .select('id')
      .single()
    
    if (retryError) {
      throw new Error(`Erro ao criar item: ${retryError.message}`)
    }
    
    return retryData.id
  }

  if (error) {
    throw new Error(`Erro ao criar item: ${error.message}`)
  }

  if (!data) {
    throw new Error('Erro ao criar item: dados não retornados')
  }

  return data.id
}

/**
 * Atualiza um item existente
 * @param id ID do item a ser atualizado
 * @param payload Dados atualizados
 * @returns Promise<void>
 */
export async function updateItem(id: string, payload: CreateInformativoPayload): Promise<void> {
  const { error } = await supabase
    .from('news')
    .update({
      date: payload.date,
      title: payload.title,
      description: payload.description,
      file: payload.file,
      link: payload.link,
      type: payload.type
    })
    .eq('id', id)
    .is('deleted_at', null) // Não permitir atualizar registros deletados

  if (error) {
    throw new Error(`Erro ao atualizar item: ${error.message}`)
  }
}

/**
 * Deleta um item (exclusão lógica)
 * @param id ID do item a ser deletado
 * @returns Promise<void>
 */
export async function deleteItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('news')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
    .is('deleted_at', null) // Apenas deletar se ainda não estiver deletado

  if (error) {
    throw new Error(`Erro ao excluir item: ${error.message}`)
  }
}

/**
 * Obtém as versões de notícias (itens mais recentes, um por “versão” no card).
 * @param type Tipo de notícia ('circulando', 'imprensa', 'interno' ou 'boletim'). Se não fornecido, considera todos os tipos.
 * @param limit Quantidade máxima de registros (default 3).
 */
export async function getVersions(
  type?: 'circulando' | 'imprensa' | 'interno' | 'boletim',
  limit = 3
): Promise<InformativoVersion[]> {
  let query = supabase
    .from('news')
    .select('id, date, title, description, file, link, created_at, deleted_at')
    .is('deleted_at', null)

  if (type) {
    query = query.eq('type', type)
  }

  query = query
    .order('date', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(limit)

  const { data, error } = await query

  if (error) {
    // Se o erro for relacionado à coluna deleted_at não existir, tentar sem o filtro
    if (error.message.includes('deleted_at') || error.message.includes('schema cache')) {
      let retryQuery = supabase.from('news').select('id, date, title, description, file, link, created_at')

      if (type) {
        retryQuery = retryQuery.eq('type', type)
      }

      retryQuery = retryQuery
        .order('date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(limit)

      const { data: retryData, error: retryError } = await retryQuery

      if (retryError) {
        throw new Error(`Erro ao carregar versões: ${retryError.message}`)
      }

      // Filtrar manualmente registros deletados (se a coluna existir nos dados)
      const filteredData = (retryData || []).filter((item: any) => !item.deleted_at)

      const versions: InformativoVersion[] = filteredData.map((item: any) => ({
        id: item.id,
        date: item.date,
        artigosCount: 1,
        title: item.title || '',
        description: item.description || '',
        file: item.file || null,
        link: item.link || null
      }))

      return versions
    }

    throw new Error(`Erro ao carregar versões: ${error.message}`)
  }

  // Filtrar manualmente registros deletados (garantia extra)
  const filteredData = (data || []).filter((item: any) => !item.deleted_at)

  const versions: InformativoVersion[] = filteredData.map((item: any) => ({
    id: item.id,
    date: item.date,
    artigosCount: 1,
    title: item.title || '',
    description: item.description || '',
    file: item.file || null,
    link: item.link || null
  }))

  return versions
}

/**
 * Resumo de curtidas para uma lista de notícias (contagem + se o usuário atual curtiu).
 */
export async function getNewsLikesSummary(
  newsIds: string[]
): Promise<Record<string, NewsLikeSummary>> {
  const unique = Array.from(new Set(newsIds.filter(Boolean)))
  const empty: Record<string, NewsLikeSummary> = {}
  for (const id of unique) empty[id] = { count: 0, likedByMe: false }
  if (unique.length === 0) return empty

  const {
    data: { user },
  } = await supabase.auth.getUser()

  const { data, error } = await supabase
    .from('news_likes')
    .select('news_id, user_id')
    .in('news_id', unique)

  if (error) throw new Error(`Erro ao carregar curtidas: ${error.message}`)

  const result = { ...empty }
  for (const row of data || []) {
    const newsId = row.news_id as string
    if (!result[newsId]) result[newsId] = { count: 0, likedByMe: false }
    result[newsId].count += 1
    if (user?.id && row.user_id === user.id) result[newsId].likedByMe = true
  }
  return result
}

/**
 * Lista quem curtiu uma notícia (nome do perfil), ordenado por data da curtida.
 */
export async function getNewsLikers(newsId: string): Promise<NewsLiker[]> {
  if (!newsId) return []

  const { data, error } = await supabase
    .from('news_likes')
    .select('user_id, created_at, profile:profiles!user_id(id, name)')
    .eq('news_id', newsId)
    .order('created_at', { ascending: true })

  if (error) throw new Error(`Erro ao carregar quem curtiu: ${error.message}`)

  return (data || []).map((row: any) => {
    const profile = Array.isArray(row.profile) ? row.profile[0] : row.profile
    const name = (profile?.name as string | null | undefined)?.trim()
    return {
      userId: (row.user_id as string) || (profile?.id as string) || '',
      name: name || 'Usuário',
    }
  }).filter((l: NewsLiker) => Boolean(l.userId))
}

/**
 * Alterna curtida do usuário atual na notícia. Retorna o resumo atualizado daquela notícia.
 */
export async function toggleNewsLike(newsId: string): Promise<NewsLikeSummary> {
  if (!newsId) throw new Error('Notícia inválida.')

  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('Usuário não autenticado')

  const { data: existing, error: existingError } = await supabase
    .from('news_likes')
    .select('id')
    .eq('news_id', newsId)
    .eq('user_id', user.id)
    .maybeSingle()

  if (existingError) throw new Error(`Erro ao verificar curtida: ${existingError.message}`)

  if (existing?.id) {
    const { error } = await supabase.from('news_likes').delete().eq('id', existing.id)
    if (error) throw new Error(`Erro ao remover curtida: ${error.message}`)
  } else {
    const { error } = await supabase
      .from('news_likes')
      .insert({ news_id: newsId, user_id: user.id })
    if (error) throw new Error(`Erro ao curtir: ${error.message}`)
  }

  const summary = await getNewsLikesSummary([newsId])
  return summary[newsId] || { count: 0, likedByMe: false }
}
